import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon } from 'lucide-react';
export interface TemplateLink {
  label: string;
  to: string;
}
interface FooterTemplateDropdownProps {
  /** Name of the template, e.g. "Guides". */
  label: string;
  /** The URL pattern this template serves, e.g. "/guide/:slug". */
  pattern: string;
  links: TemplateLink[];
}

/**
 * One collapsible list of every URL a route template currently serves.
 *
 * These lists are long and only matter to someone looking for a specific page,
 * so they stay collapsed by default — expanding four flat lists of nine to
 * thirteen links each would bury the rest of the footer.
 */
export function FooterTemplateDropdown({
  label,
  pattern,
  links
}: FooterTemplateDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click and on Escape, so an open list never traps the user.
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);
  const scrollToTop = () => window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
  if (links.length === 0) return null;
  return <div className="relative" ref={containerRef} data-id="element-260" data-mp="15908huyu3ps1">
      <button type="button" onClick={() => setIsOpen(open => !open)} aria-expanded={isOpen} aria-haspopup="true" className="w-full flex items-center justify-between gap-3 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 border border-white/10 rounded-md text-gray-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white/30" data-id="element-261" data-mp="2unobx6pg5xdx">
        <span className="flex items-baseline gap-2 min-w-0" data-id="element-262" data-mp="2o6wlzoxc6wpm">
          <span className="truncate" data-id="element-263" data-mp="2o98xpkci4au8">{label}</span>
          <span className="text-xs text-gray-500 flex-shrink-0" data-id="element-264" data-mp="2j8v0yygconbi">
            {links.length}
          </span>
        </span>
        <ChevronDownIcon className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-265" data-mp="3guio0sqx1tad" />
      </button>

      {isOpen && <div className="absolute z-30 left-0 right-0 bottom-full mb-2 bg-navy-dark border border-white/10 rounded-md shadow-xl overflow-hidden" data-id="element-266" data-mp="373nxhc6n07j4">
          <p className="px-3 pt-2 pb-1.5 text-[11px] font-mono text-gray-500 border-b border-white/10" data-id="element-267" data-mp="3h78pgca7iz1">
            {pattern}
          </p>
          <ul className="max-h-72 overflow-y-auto py-1" data-id="element-268" data-mp="3uti9xsn1nu2u">
            {links.map(link => <li key={link.to} data-id="element-269" data-mp="636g6ug7pdjd">
                <Link to={link.to} onClick={() => {
            setIsOpen(false);
            scrollToTop();
          }} className="block px-3 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-colors duration-200" data-id="element-270" data-mp="b06nt9spmocl">
                  {link.label}
                </Link>
              </li>)}
          </ul>
        </div>}
    </div>;
}